import React from 'react';
import PropTypes from 'prop-types';
import { GatsbyImage } from "gatsby-plugin-image";
import classnames from 'classnames';
import { isGatsbyImageData, getAspectRatio } from '~components/image-utils';

import Lightbox from '~components/Lightbox';

import './Gallery.scss';

// Renders only Image children, and sets up an auto-layout such that all children are in a row on desktop, and stacked on mobile.
class Gallery extends React.Component {
  
  constructor(props) {
    super(props);

    this.state = {
      activePreview: undefined
    }

    this.flipPreview = this.flipPreview.bind(this);
    this.closePreview = this.closePreview.bind(this);
  }

  flipPreview(image) {
    this.setState({ activePreview: image });
  }

  closePreview() {
    this.setState({ activePreview: undefined });
  }

  render() {
    const { children, caption, itemsPerRow, allowWrap, imgs, style } = this.props;
    
    return (
      <div
        style={style}
        className={classnames(
          'gallery',
          {
            'gallery--wrapping': !!itemsPerRow || allowWrap
          }
        )}
      >
        <div className="tablet-and-desktop">
          {this.state.activePreview && (
            <Lightbox onClose={this.closePreview}>
              <div className="gallery__preview-content">
                <div className="gallery__preview-image-container">
                  <div className="gallery__preview-image" style={{ maxWidth: this.state.activePreview.presentationWidth || '70%' }}>
                    {isGatsbyImageData(this.state.activePreview) ? (
                      <GatsbyImage
                        image={this.state.activePreview}
                        imgStyle={{ objectFit: 'contain' }}
                        style={{ position: 'absolute', height: '100%', width: '100%' }}
                      />
                    ) : this.state.activePreview.publicURL ? (
                      <img src={this.state.activePreview.publicURL} style={{ position: 'absolute', height: '100%', width: '100%', objectFit: 'contain' }} />
                    ) : null}
                  </div>
                  {caption && (
                    <p className="caption monospace full-width gallery__preview-caption">{caption}</p>
                  )}
                </div>
                <div className="gallery__preview-strip">
                  {React.Children.map(children, (child) => {
                    if(child.props && child.props.name && imgs[child.props.name]) {
                      const image = imgs[child.props.name];
                      return (
                        <div className="gallery__preview-thumbnail" onClick={() => this.flipPreview(image)}>
                          {isGatsbyImageData(image) ? (
                            <GatsbyImage image={image} imgStyle={{ objectFit: 'contain' }} style={{ width: `calc(${getAspectRatio(image)} * 7rem)` }} />
                          ) : image.publicURL ? (
                            <img src={image.publicURL} />
                          ) : null}
                        </div>
                      );
                    }
                  })}
                </div>
              </div>
            </Lightbox>
          )}
        </div>
        <div className="gallery__body">
          {React.Children.map(children, (child) => {
            if(child.props && child.props.name && imgs[child.props.name]) {
              const image = imgs[child.props.name];

              const standardItem = (
                <div
                  className="gallery__item"
                  onClick={() => this.flipPreview(image)}
                  style={{
                    flexGrow: getAspectRatio(image),
                    flexBasis: itemsPerRow && `calc(${100 / itemsPerRow}% - 16px)`
                  }}
                >
                  {React.cloneElement(child, { disablePreview: true })}
                </div>
              );

              if (!itemsPerRow || typeof itemsPerRow === 'number') {
                return standardItem;
              }
              
              if(itemsPerRow.tablet && itemsPerRow.desktop) {
                return (
                  <>
                    <div
                      className="gallery__item mobile"
                      onClick={() => this.flipPreview(image)}
                    >
                      {React.cloneElement(child, { disablePreview: true })}
                    </div>
                    <div
                      className="gallery__item tablet"
                      onClick={() => this.flipPreview(image)}
                      style={{
                        flexGrow: getAspectRatio(image),
                        flexBasis: (itemsPerRow.tablet && (itemsPerRow.tablet !== 'all')) && `calc(${100 / itemsPerRow.tablet}% - 16px)`
                      }}
                    >
                      {React.cloneElement(child, { disablePreview: true })}
                    </div>
                    <div
                      className="gallery__item desktop"
                      onClick={() => this.flipPreview(image)}
                      style={{
                        flexGrow: getAspectRatio(image),
                        flexBasis: (itemsPerRow.desktop && (itemsPerRow.desktop !== 'all')) && `calc(${100 / itemsPerRow.desktop}% - 16px)`
                      }}
                    >
                      {React.cloneElement(child, { disablePreview: true })}
                    </div>
                  </>
                )
              }

              return standardItem;
              
            }
          })}
        </div>
        {caption && (
          <p className="monospace caption gallery__caption">{caption}</p>
        )}
      </div>
    );
  }
}

Gallery.propTypes = {
  caption: PropTypes.string,
  itemsPerRow: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({
      desktop: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.oneOf(['all'])
      ]),
      tablet: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.oneOf(['all'])
      ])
    })
  ]),
  style: PropTypes.instanceOf(Object),
  allowWrap: PropTypes.bool
}

export default Gallery;
